import { useCallback, useId, useRef, type ReactNode } from 'react';

import { Button } from './Button';
import { useFocusTrap } from './useFocusTrap';

export interface ModalProps {
  /** Whether the dialog is on screen. */
  readonly open: boolean;
  /** Called on Escape, on the close button, and on a click outside the panel. */
  readonly onClose: () => void;
  /** The dialog's accessible name; rendered as its heading. */
  readonly title: string;
  /** Optional supporting line under the title, announced with the dialog. */
  readonly description?: string;
  /** Footer controls, right-aligned. */
  readonly footer?: ReactNode;
  /** `sm` for confirmations, `md` for forms. */
  readonly size?: 'sm' | 'md';
  /** The body. */
  readonly children: ReactNode;
}

/**
 * A modal dialog.
 *
 * Deliberately not the native `<dialog>` element: `showModal()` is imperative,
 * so opening becomes an effect that has to be kept in step with React state,
 * and its behaviour under jsdom is incomplete enough that the interaction tests
 * would be testing a polyfill rather than the component. A `role="dialog"` with
 * `aria-modal`, a real focus trap ({@link useFocusTrap}) and a scrim gives the
 * same guarantees and stays declarative.
 *
 * @param props see {@link ModalProps}
 * @returns the dialog, or nothing when closed
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  children,
}: ModalProps): React.JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const base = useId();
  const titleId = `${base}-title`;
  const descriptionId = `${base}-description`;

  const handleEscape = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleKeyDown = useFocusTrap(panelRef, open, handleEscape);

  if (!open) {
    return null;
  }

  return (
    <div className="fo-overlay" data-variant="modal">
      {/* A scrim is decoration, so it is not a button and carries no role; the
          dialog is dismissed by Escape and by the close control, and clicking
          the scrim is an extra convenience rather than the only way out. */}
      <div
        className="fo-overlay__scrim"
        aria-hidden="true"
        onClick={() => {
          onClose();
        }}
      />
      <div
        ref={panelRef}
        className="fo-modal"
        data-size={size}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {/* See Drawer: <header>/<footer> inside a role="dialog" would map to
            the banner and contentinfo landmarks, which belong to the page. */}
        <div className="fo-modal__header">
          <div>
            <h2 className="fo-modal__title" id={titleId}>
              {title}
            </h2>
            {description === undefined ? null : (
              <p className="fo-modal__description" id={descriptionId}>
                {description}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" icon="close" iconOnly onClick={onClose}>
            Close
          </Button>
        </div>

        <div className="fo-modal__body">{children}</div>

        {footer === undefined ? null : <div className="fo-modal__footer">{footer}</div>}
      </div>
    </div>
  );
}
